const NON_REFUNDABLE_SKUS = ["FIRELITE-NT-90", "WIRE45", "PPF90"];

class TrudoorProductForm extends HTMLElement {
  constructor() {
    super();
    this.state = {
      currentVariant: null,
    };
  }

  connectedCallback() {
    // Check URL variant against current variant first
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get("variant");
    const currentVariantId = this.dataset.variantId;

    // Only proceed with initialization if URL variant doesn't match current
    if (urlVariantId && urlVariantId !== currentVariantId) {
      const variant = this.getVariantData().find((v) => v.id === parseInt(urlVariantId));
      if (variant) {
        this.state.currentVariant = variant;
        this.updateSelectsForVariant(variant);
      }
    }

    // Initialize elements
    this.quantityInput = document.querySelector(`input#quantity-${this.dataset.productId}`);
    this.addToCartButton = document.querySelector("[type=submit]#add");
    // Only select variant dropdowns, not add-on selects
    this.variantSelects = this.querySelectorAll("product-variant-option select");
    this.swatches = this.querySelectorAll("product-variant-option .variable-item");

    // Prevent default form submission
    const form = this.closest("form");
    if (form) {
      form.removeAttribute("action");
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    }

    // Only set initial state if we haven't already from URL
    if (!urlVariantId || urlVariantId === currentVariantId) {
      this.state.currentVariant = {
        id: currentVariantId,
        sku: this.dataset.variantSku,
      };
      this.updateSelectsForVariant(this.state.currentVariant);
    }

    // Bind events
    this.addToCartButton?.addEventListener("click", this.handleAddToCart.bind(this));
    this.addEventListener("change", this.onVariantChange.bind(this));
    this.swatches.forEach((swatch) => {
      swatch.addEventListener("click", this.handleSwatchClick.bind(this));
    });

    // Update available options on initial load
    this.updateAvailableOptions();
  }

  // New method to set initial variant state
  updateSelectsForVariant(variant) {
    const variantData = this.getVariantData().find((v) => v.id === parseInt(variant.id));
    if (!variantData) return;

    // Update each select to match the variant's options
    this.variantSelects.forEach((select, index) => {
      const option = variantData.options[index];
      if (option) {
        // Only update select value if the option exists in the select
        const optionExists = Array.from(select.options).some((opt) => opt.value === option);
        if (optionExists) {
          select.value = option;

          // Escape special characters in the selector
          const escapedValue = CSS.escape(option);
          const swatch = this.querySelector(
            `.variable-item[data-select-id="${select.id}"][data-value="${escapedValue}"]`,
          );

          if (swatch) {
            this.updateActiveSwatch(swatch);
          }
        }
      }
    });
  }

  async handleAddToCart(e) {
    e.preventDefault();
    const quantity = parseInt(this.quantityInput.value, 10) || 1;
    const cartItems = [];
    const addToCartButton = this.querySelector(".single_add_to_cart_button");

    // Show loading state
    LoadingManager.show({ elementId: this.id });

    try {
      const cutToSize = document.querySelector("trudoor-product-cut-to-size");
      const addOn = document.querySelector("trudoor-product-add-on");

      // Build the main product first so its SKU can be handed to the add-on
      let mainItem = null;
      if (cutToSize) {
        mainItem = cutToSize.getCartData(quantity);
      } else {
        const sku = this.dataset.variantSku;
        const properties = {
          SKU: sku,
          "Usually Ships": this.dataset.variantUsuallyShips,
          _dimensions_width: this.dataset.variantMetafieldDimensionsWidth,
          _dimensions_length: this.dataset.variantMetafieldDimensionsLength,
          _dimensions_height: this.dataset.variantMetafieldDimensionsHeight,
        };
        if (NON_REFUNDABLE_SKUS.includes(sku)) {
          properties["Non-refundable"] = "True";
        }
        mainItem = {
          id: parseInt(this.dataset.variantId, 10),
          quantity,
          properties,
        };
      }

      // Add add-on FIRST so it appears BELOW main product in cart (Shopify shows newest on top)
      if (addOn) {
        const data = addOn.getCartData(quantity, mainItem?.properties.SKU);
        if (data) {
          cartItems.push(data);
          // Flag the host line so a labeled item never merges with an unlabeled line of
          // the same variant — the cart keys the label's quantity off this line.
          if (mainItem && addOn.state.productType === "label") {
            mainItem.properties._labeled = "true";
          }
        }
      }

      // Add main product LAST so it appears on top in cart
      if (mainItem) cartItems.push(mainItem);

      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: cartItems }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Cart API error:", errorData);
        throw new Error(errorData.description || "Failed to add to cart");
      }

      const addedItems = await response.json();

      // Update mini cart count
      const cartResponse = await fetch(window.Shopify.routes.root + "cart.js");
      const cartData = await cartResponse.json();

      // Show success state
      if (addToCartButton) {
        addToCartButton.classList.add("added");
        setTimeout(() => addToCartButton.classList.remove("added"), 2000);
      }

      const html = await SectionRenderingApi.fetchSection({ id: "trudoor-header" });
      const quoteCartButton = html.querySelector("#quote-cart-button");
      if (quoteCartButton) {
        document.querySelector(`#${quoteCartButton.id}`).innerHTML = quoteCartButton.innerHTML;
      }

      // Update mini cart
      const miniCart = document.querySelector("mini-cart");
      if (miniCart) {
        miniCart.handleCartUpdate({
          detail: {
            newItems: addedItems.items,
            cart: cartData,
          },
        });
        miniCart.open();
      }

      // Reset components
      cutToSize?.reset();
      addOn?.reset();
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      // Hide loading state
      LoadingManager.hide({ elementId: this.id });
    }
  }

  handleSwatchClick(event) {
    const swatch = event.currentTarget;

    // Don't allow clicking disabled swatches
    if (swatch.classList.contains("disabled") || swatch.classList.contains("unavailable")) {
      return;
    }

    const select = document.getElementById(swatch.dataset.selectId);

    if (select) {
      select.value = swatch.dataset.value;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      this.updateActiveSwatch(swatch);
    }
  }

  onVariantChange(event) {
    // Skip non-variant changes
    if (
      event.target.name === "add_on_product" ||
      event.target.name === "attribute_add_on" ||
      event.target.name === "quantity"
    ) {
      return;
    }

    // Update available options based on current selections
    this.updateAvailableOptions();

    const options = Array.from(this.variantSelects, (select) => select.value);
    const variant = this.getVariantData().find((variant) =>
      variant.options.every((option, index) => option === options[index]),
    );

    if (variant && this.state.currentVariant?.id !== variant.id) {
      this.state.currentVariant = variant;

      // Dispatch variant change event with a callback
      this.dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: {
            variant,
            shouldUpdateMedia: true,
            onComplete: () => {
              // Optionally handle any post-update logic here
            },
          },
        }),
      );
    }
  }

  updateAvailableOptions() {
    const variantData = this.getVariantData();
    const selects = Array.from(this.variantSelects);

    selects.forEach((select, selectIndex) => {
      // Get currently selected values for all previous options
      const previousSelections = selects.slice(0, selectIndex).map((s) => s.value);

      // Filter variants that match all previous selections
      const matchingVariants = variantData.filter((variant) => {
        return previousSelections.every((selection, i) => {
          return !selection || variant.options[i] === selection;
        });
      });

      // Get available values for this option from matching variants
      const availableValues = new Set(matchingVariants.map((v) => v.options[selectIndex]));

      // Update each option in the select
      Array.from(select.options).forEach((option) => {
        if (!option.value) return; // Skip placeholder option

        const isAvailable = availableValues.has(option.value);
        option.disabled = !isAvailable;
        option.classList.toggle("unavailable", !isAvailable);

        // Update text to show unavailable status
        const baseValue = option.dataset.originalValue || option.value;
        if (!option.dataset.originalValue) {
          option.dataset.originalValue = option.value;
        }
        option.textContent = isAvailable ? baseValue : `${baseValue} - Unavailable`;
      });

      // Also update corresponding swatches
      const swatchesForSelect = this.querySelectorAll(`.variable-item[data-select-id="${select.id}"]`);
      swatchesForSelect.forEach((swatch) => {
        const isAvailable = availableValues.has(swatch.dataset.value);
        swatch.classList.toggle("unavailable", !isAvailable);
        swatch.classList.toggle("disabled", !isAvailable);
      });
    });
  }

  updateActiveSwatch(selectedSwatch) {
    this.swatches.forEach((swatch) => {
      swatch.setAttribute("aria-checked", swatch === selectedSwatch);
      swatch.classList.toggle("selected", swatch === selectedSwatch);
    });
  }

  getVariantData() {
    if (!this.variantData) {
      try {
        this.variantData = JSON.parse(this.dataset.variantData);
      } catch (error) {
        console.error("Error parsing variant data:", error);
        this.variantData = [];
      }
    }
    return this.variantData;
  }

  // async updateSectionContent(variant, isCartUpdate = false) {
  //   const sectionId = this.dataset.originalSection || this.dataset.section;
  //   const response = await fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${sectionId}`);
  //   const html = new DOMParser().parseFromString(await response.text(), "text/html");

  //   // Update gallery only for variant changes, not cart updates
  //   if (!isCartUpdate) {
  //     const gallery = document.getElementById("trudoor-product-gallery");
  //     const newGallery = html.getElementById("trudoor-product-gallery");
  //     if (gallery && newGallery) {
  //       gallery.innerHTML = newGallery.innerHTML;
  //       gallery.reinitialize();
  //     }
  //   }

  //   // Update form
  //   const form = document.getElementById(`product_form_${this.dataset.productId}`);
  //   const newForm = html.getElementById(`product_form_${this.dataset.productId}`);
  //   if (form && newForm) {
  //     form.innerHTML = newForm.innerHTML;
  //   }

  //   this.quantityInput.value = 1;
  // }
}

customElements.define("trudoor-product-form", TrudoorProductForm);
